// Teams (planner), met de markup van het prototype (Teams in design/build/prototype.js, proto-teams-logic.js).
// Elke persoon zit in hoogstens één team; teams groeperen het bord. Per persoon en week het deel van de werkweek dat
// inzetbaar is voor de planning (standaard 100 %); een nieuwere periode gaat voor op een oudere.
// Met Clockify: verlof en feestdagen in het raster, de kolom Planbaar (uren en bezetting volgende week), de werkweek en
// supportreservering per persoon, en in de lade wat een nieuwe periode doet met de planbare uren en de bezetting.
// Zonder Clockify (of voor wie er niet in staat) alleen de inzetbaarheid, zoals voorheen.
import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router";
import { fmtHours, useClockifyLive, type Absence, type CapPerson } from "../lib/api-clockify";
import {
  absenceText, absentOn, INZET_KINDS, INZET_PCTS, mergeAbsences, NO_TEAM, nOf, normalWeek, periodAt, periodText, spanText, teamOfPerson, teamsApi,
  TONE_CLS, useCapacityView, useTeams, useTeamWrite, whenText, type CapView, type InzetPeriod, type Team, type Week, weeksFrom,
} from "../lib/api-teams";
import { catOf } from "../lib/board";
import { useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { ddmm, firstName } from "../lib/planning";
import type { Person } from "../lib/types";
import { Av } from "../components/Avatar";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";

const SHORT: Record<string, string> = { "Losstaand project": "project", Opleiding: "opleiding", "Intern werk": "intern", Anders: "anders" };
// een andere inzetbaarheid verandert ook de capaciteit
const KEYS = [["teams"], ["capacity"]];
// de blauwe reeks van het prototype kent 80, 60, 50, 40, 20 en 0 %
const shade = (pct: number) => (pct >= 80 ? 80 : pct >= 60 ? 60 : pct >= 50 ? 50 : pct >= 40 ? 40 : pct >= 20 ? 20 : 0);
const r1 = (h: number) => Math.round(h * 10) / 10;
// status bij de avatar: bezig volgens de timer, vandaag afwezig, of in Clockify maar niet bezig
const DOT: Record<string, string> = { bezig: "nu bezig volgens de Clockify-timer", afwezig: "vandaag afwezig", laatst: "geen lopende timer" };

/** Een hele week weg: verlof als er verlof bij is, anders vrij (feestdag of sluiting). */
const offWord = (abs: Absence[]) => (abs.some((a) => a.kind === "verlof") ? "verlof" : "vrij");
const dash = (abs: Absence[]) => { const a = abs[0].from, b = abs[abs.length - 1].to; return a === b ? ddmm(a) : ddmm(a) + "–" + ddmm(b); };

/** Een week van een persoon met capaciteit: planbare uren, de uren zonder inzetbaarheid (base) en de afwezigheden. */
function capWeek(cv: CapView, cp: CapPerson, monday: string, pct: number, normal: number) {
  const i = cv.weekOf(monday);
  if (i < 0) return null;
  const w = cp.weeks[i];
  // capaciteit = base maal inzetbaarheid; bij 0 % is de base niet af te leiden en nemen we de gewone werkweek
  const off = w.cap === 0 && pct > 0 && w.absent.length > 0;
  const base = off ? 0 : pct > 0 ? (w.cap * 100) / pct : normal;
  return { i, w, off, base };
}

type Write = ReturnType<typeof useTeamWrite>;

export function Teams() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const tq = useTeams();
  const write = useTeamWrite();
  const cv = useCapacityView(me?.role === "planner");
  const { data: live } = useClockifyLive();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<string | null>(null);
  const [teamEdit, setTeamEdit] = useState<{ team: Team | null } | null>(null);

  const people = useMemo(() => [...(data?.people || [])].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  if (me && me.role !== "planner") {
    return (
      <>
        <div className="p-head"><div><h1>Teams</h1><p>Voor planners</p></div></div>
        <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>Teams en inzetbaarheid richt de planner in.</p></div>
      </>
    );
  }
  if ((!data && error) || (!tq.data && tq.error)) return <LoadError what="Teams" onRetry={() => { refetch(); tq.refetch(); }} />;
  if (!data || !me || !tq.data) return <div className="p-loading">Teams laden…</div>;

  const { teams, inzet, log } = tq.data;
  const weeks = weeksFrom(data.today);
  const teamOf = teamOfPerson(teams);
  const byId = new Map(people.map((p) => [p.id, p]));
  const actief = (pid: string) => data.tasks.filter((t) => catOf(t) === "actief" && t.owners.some((o) => o.personId === pid)).length;
  const qq = q.trim().toLowerCase();

  // per team de mensen, Zonder team achteraan (alleen als er iemand zonder team is)
  // met een zoekterm: de mensen of het team die erop passen
  const groups = [...teams.map((t) => ({ team: t as Team | null, name: t.name })), { team: null, name: NO_TEAM }]
    .map((g) => ({ ...g, people: people.filter((p) => (teamOf.get(p.id)?.id ?? null) === (g.team?.id ?? null)) }))
    .filter((g) => g.team || g.people.length)
    .map((g) => ({ ...g, list: g.people.filter((p) => !qq || p.name.toLowerCase().includes(qq) || g.name.toLowerCase().includes(qq)) }))
    .filter((g) => !qq || g.list.length);

  const removePeriod = (e: InzetPeriod) => write.run({
    run: () => teamsApi.removeInzet(e.id),
    undo: () => teamsApi.addInzet({ personId: e.personId, from: e.from, to: e.to, pct: e.pct, kind: e.kind, note: e.note }),
    keys: KEYS,
  }).catch(() => {});

  const active = inzet.filter((e) => e.pct < 100 && (!e.to || e.to >= weeks[0].monday))
    .sort((a, b) => a.from.localeCompare(b.from) || (byId.get(a.personId)?.name || "").localeCompare(byId.get(b.personId)?.name || ""));
  const selPerson = sel ? byId.get(sel) : null;

  // ---------- capaciteit uit Clockify ----------
  // de kolom Planbaar staat er zodra Clockify capaciteit levert; zonder: acht weken naast de naam (p-tm-8)
  const withCap = !!cv && cv.weekOf(weeks[1].monday) >= 0;
  const g8 = withCap ? "" : " p-tm-8";
  const pctOf = (pid: string) => (monday: string) => periodAt(inzet, pid, monday)?.pct ?? 100;
  const lv = live && live.clockify.state !== "uit" ? live : null;
  const busy = new Set(lv?.timers.map((t) => t.personId));
  const dotOf = (pid: string, cp: CapPerson | undefined) =>
    busy.has(pid) ? "bezig" : cv && cp && absentOn(cv, cp, data.today) ? "afwezig" : lv?.linked.includes(pid) ? "laatst" : null;
  /** volgende week: planbaar, de uren zonder inzetbaarheid en de geplande uren */
  const next = (pid: string) => {
    const cp = cv?.people.get(pid);
    if (!cv || !cp || !withCap) return null;
    const pct = pctOf(pid)(weeks[1].monday), x = capWeek(cv, cp, weeks[1].monday, pct, normalWeek(cv, cp, pctOf(pid)))!;
    return { cp, x, ...cv.load(cp, x.i) };
  };
  const total = (ids: string[]) => {
    const xs = ids.map(next).filter((x) => x !== null);
    const cap = xs.reduce((a, x) => a + x.cap, 0), base = xs.reduce((a, x) => a + x.x.base, 0), load = xs.reduce((a, x) => a + x.load, 0);
    return { n: xs.length, cap, base, pct: cap > 0 ? Math.round((load / cap) * 100) : null };
  };
  const all = withCap ? total(people.map((p) => p.id)) : null;
  const nextCell = (pid: string) => {
    const x = next(pid);
    if (!x) return <span className="p-tm-next t-mute"><b>–</b><small>niet in Clockify</small></span>;
    const sub = x.pct != null ? x.pct + " % bezet" : x.x.w.absent.length ? offWord(x.x.w.absent) : "niet inzetbaar";
    return <span className={"p-tm-next " + (x.pct != null ? TONE_CLS[cv!.tone(x.pct)] : "t-mute")}><b>{fmtHours(x.cap)}</b><small>{sub}</small></span>;
  };
  // onder de naam: met Clockify de werkweek en supportreservering (zoals het prototype), anders de actieve taken
  const sub = (p: Person, n: number) => {
    const cp = cv?.people.get(p.id);
    if (!cv || !cp) return nOf(n, "actieve taak", "actieve taken");
    return fmtHours(normalWeek(cv, cp, pctOf(p.id)), 0) + "/w" + (cp.supportPerWeek ? " · support ≈ " + fmtHours(cp.supportPerWeek, 0) : "");
  };
  // een cel van het raster: verlof en feestdagen uit Clockify, de inzetbaarheid uit Teams
  const cell = (p: Person, w: Week, normal: number | null) => {
    const e = periodAt(inzet, p.id, w.monday), pct = e ? e.pct : 100;
    const cp = cv?.people.get(p.id), x = cv && cp && normal != null ? capWeek(cv, cp, w.monday, pct, normal) : null;
    const why = e && pct < 100 ? "; " + e.kind.toLowerCase() + (e.note ? ", " + e.note : "") : "";
    if (x?.off) {
      return <span key={w.monday} className="p-tm-c off" title={p.name + " · " + w.label + ": " + x.w.absent.map(absenceText).join(", ")}>{offWord(x.w.absent)}<small>{dash(x.w.absent)}</small></span>;
    }
    const abs = x ? x.w.absent : [];
    const small = pct < 100 ? SHORT[e!.kind] || e!.kind.toLowerCase() : abs.length ? (abs.some((a) => a.kind === "verlof") ? "verlof" : ddmm(abs[0].from) + " vrij") : "";
    const tip = p.name + " · " + w.label + ": " + pct + " % inzetbaar" + (x ? ", " + fmtHours(x.w.cap).replace(/ u$/, "") + " van " + fmtHours(x.base) + " planbaar" : "") + why + (abs.length ? "; " + abs.map(absenceText).join(", ") : "");
    return (
      <span key={w.monday} className={"p-tm-c" + (pct < 100 ? " less p" + shade(pct) : "")} title={tip}>
        {pct} %{small && <small>{small}</small>}
      </span>
    );
  };

  return (
    <>
      <div className="p-head">
        <div><h1>Teams</h1><p>{nOf(teams.length, "team", "teams")} · {nOf(people.length, "persoon", "personen")}{all && all.n ? " · " + weeks[1].label + ": " + Math.round(all.cap) + " van " + fmtHours(Math.round(all.base), 0) + " planbaar" : ""} · alleen planners wijzigen dit</p></div>
        <div className="p-tools">
          <label className="ex-search p-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek persoon of team" aria-label="Zoek persoon of team" value={q} onChange={(e) => setQ(e.target.value)} /></label>
          <button type="button" className="ex-btn ex-btn--primary" onClick={() => { setSel(null); setTeamEdit({ team: null }); }}><Icon name="plus" size={15} />Nieuw team</button>
        </div>
      </div>
      <div className="p-tm">
        <section className="p-card p-tm-main">
          <div className="p-card-h"><div><h2>Inzetbaar voor de planning</h2><p>Per week het deel van de werkweek dat beschikbaar is voor taken en support · klik op een persoon om het aan te passen</p></div></div>
          <div className="p-tm-scroll">
            <div className={"p-tm-row p-tm-hd" + g8}><span className="p">Persoon</span>{weeks.map((w, i) => <span key={w.monday} className={i === 0 ? "now" : ""}>{w.label}<small>{w.sub}</small></span>)}{withCap && <span>Planbaar {weeks[1].label}</span>}</div>
            {groups.map(({ team: gt, name, people: members, list }) => {
              const sub2 = !members.length ? "nog geen leden" : list.length < members.length ? list.length + " van " + nOf(members.length, "persoon", "personen") + " in beeld" : nOf(members.length, "persoon", "personen");
              const tt = withCap ? total(list.map((p) => p.id)) : null;
              return (
                <Fragment key={gt?.id ?? "geen"}>
                  <div className={"p-tm-row p-tm-team" + g8}>
                    <span className="p"><span className="nm"><b>{name}</b><small>{sub2}</small></span></span>
                    <span className="p-tm-span">{gt && <button type="button" className="ex-btn-xs" onClick={() => { setSel(null); setTeamEdit({ team: gt }); }}>Team wijzigen</button>}</span>
                    {tt && (
                      <span className={"p-tm-next " + (tt.pct != null ? TONE_CLS[cv!.tone(tt.pct)] : "t-mute")}>
                        <b>{tt.base > 0 ? Math.round(tt.cap) + " van " + fmtHours(Math.round(tt.base), 0) : "–"}</b><small>{tt.pct != null ? tt.pct + " % bezet" : ""}</small>
                      </span>
                    )}
                  </div>
                  {list.map((p) => {
                    const n = actief(p.id), cp = cv?.people.get(p.id), dot = dotOf(p.id, cp);
                    const normal = cv && cp ? normalWeek(cv, cp, pctOf(p.id)) : null;
                    return (
                      <button key={p.id} type="button" className={"p-tm-row p-tm-p" + g8} onClick={() => { setTeamEdit(null); setSel(p.id); }}>
                        <span className="p">
                          <span className="p-avwrap"><Av id={p.id} init={p.initials} />{dot && <span className={"st " + dot} title={DOT[dot]} />}</span>
                          <span className="nm"><b>{p.name}</b><small title={cp ? nOf(n, "actieve taak", "actieve taken") : undefined}>{sub(p, n)}</small></span>
                        </span>
                        {weeks.map((w) => cell(p, w, normal))}
                        {withCap && nextCell(p.id)}
                      </button>
                    );
                  })}
                </Fragment>
              );
            })}
            {!groups.length && <p className="p-empty">{qq ? "Niets gevonden." : "Nog niemand op de planning."}</p>}
          </div>
          <div className="p-tm-leg">
            <span>Inzetbaar</span><span className="p-tm-sw"><i className="w" />100 %</span><span className="p-tm-sw"><i className="b0" />80 %</span><span className="p-tm-sw"><i className="b1" />60 %</span>
            <span className="p-tm-sw"><i className="b2" />50 tot 40 %</span><span className="p-tm-sw"><i className="b3" />20 %</span><span className="p-tm-sw"><i className="b4" />0 %</span>
            <span className="p-tm-leg-r">Telt mee in Capaciteit</span>
          </div>
        </section>
        <div className="p-tm-side">
          <section className="p-card">
            <div className="p-card-h"><div><h2>Minder inzetbaar</h2><p>Lopende en geplande perioden</p></div></div>
            <div className="p-tm-list">
              {active.map((e) => {
                const p = byId.get(e.personId);
                return (
                  <div key={e.id} className="p-tm-li">
                    <Av id={e.personId} init={p?.initials || "?"} cls="p-av s" />
                    <button type="button" className="t" onClick={() => { if (p) { setTeamEdit(null); setSel(p.id); } }}>
                      <b>{p?.name || "Niet op de planning"}</b><span>{e.pct} % inzetbaar · {periodText(e)}</span><small>{e.kind}{e.note ? ": " + e.note : ""}</small>
                    </button>
                    <button type="button" className="ex-btn-xs" disabled={write.busy} onClick={() => removePeriod(e)}>Verwijderen</button>
                  </div>
                );
              })}
              {!active.length && <p className="p-empty p-tm-empty">Iedereen is volledig inzetbaar.</p>}
            </div>
          </section>
          <section className="p-card p-kp-log">
            <div className="p-card-h"><div><h2>Laatste wijzigingen</h2><p>Met naam en tijd</p></div></div>
            <div className="p-kp-log-b">
              {log.slice(0, 6).map((x) => <div key={x.id} className="p-kp-log-i"><b>{x.text}</b><span>{whenText(x.at, data.today)} · {x.who}</span></div>)}
              {!log.length && <p className="p-empty p-tm-empty">Nog geen wijzigingen.</p>}
            </div>
          </section>
        </div>
      </div>
      {selPerson && <PersonDrawer key={selPerson.id} person={selPerson} teams={teams} team={teamOf.get(selPerson.id) || null} inzet={inzet} weeks={weeks} write={write} onRemove={removePeriod} onClose={() => setSel(null)}
        cv={withCap ? cv : null} cp={cv?.people.get(selPerson.id) || null} />}
      {teamEdit && <TeamDrawer key={teamEdit.team?.id ?? "nieuw"} team={teamEdit.team} teams={teams} people={people} teamOf={teamOf} write={write} onClose={() => setTeamEdit(null)} />}
    </>
  );
}

interface Form { from: number; to: number | "open"; pct: number; kind: string; note: string }
const blank = (): Form => ({ from: 1, to: 1, pct: 50, kind: INZET_KINDS[0], note: "" });

function PersonDrawer({ person, teams, team, inzet, weeks, write, onRemove, onClose, cv, cp }: {
  person: Person; teams: Team[]; team: Team | null; inzet: InzetPeriod[]; weeks: Week[]; write: Write; onRemove: (e: InzetPeriod) => void; onClose: () => void;
  /** capaciteit uit Clockify; null zonder Clockify. cp: deze persoon, null als die niet in Clockify staat */
  cv: CapView | null; cp: CapPerson | null;
}) {
  const [f, setF] = useState<Form>(blank);
  const set = (patch: Partial<Form>) => setF({ ...f, ...patch });
  const pid = person.id;
  const mine = inzet.filter((e) => e.personId === pid && (!e.to || e.to >= weeks[0].monday)).sort((a, b) => a.from.localeCompare(b.from));
  const next = periodAt(inzet, pid, weeks[1].monday), nextPct = next ? next.pct : 100;
  const to = f.to === "open" ? weeks.length - 1 : Math.max(f.from, f.to);
  const kind = f.kind.toLowerCase();

  // met Clockify: planbare uren en bezetting volgende week, en per week van de nieuwe periode wat ze wordt
  const pctOf = (monday: string) => periodAt(inzet, pid, monday)?.pct ?? 100;
  const normal = cv && cp ? normalWeek(cv, cp, pctOf) : 0;
  const capAt = (monday: string, pct: number) => (cv && cp ? capWeek(cv, cp, monday, pct, normal) : null);
  const n1 = capAt(weeks[1].monday, nextPct), l1 = cv && cp && n1 ? cv.load(cp, n1.i) : null;
  const pill = !n1 || !l1 ? { cls: nextPct < 100 ? "warn" : "ok", text: weeks[1].label + ": " + nextPct + " % inzetbaar" }
    : l1.cap === 0 ? { cls: "warn", text: weeks[1].label + ": " + (n1.w.absent.length ? offWord(n1.w.absent) : "niet planbaar") }
    : {
      cls: l1.pct != null && cv!.tone(l1.pct) === "overvol" ? "err" : l1.pct != null && cv!.tone(l1.pct) === "vol" ? "warn" : "ok",
      text: weeks[1].label + ": " + fmtHours(l1.cap) + " planbaar" + (nextPct < 100 ? ", " + nextPct + " % inzetbaar" : "") + (l1.pct != null ? " · " + l1.pct + " % bezet" : ""),
    };
  const effCap = weeks.slice(f.from, to + 1).map((w) => {
    const x = capAt(w.monday, pctOf(w.monday));
    if (!cv || !cp || !x) return null;
    // de bezetting met de nieuwe capaciteit, op dezelfde manier gerekend als overal (weekLoad)
    const cap = (x.base * f.pct) / 100;
    const sim = cv.load({ ...cp, weeks: cp.weeks.map((y, j) => (j === x.i ? { ...y, cap } : y)) }, x.i);
    const gone = offWord(x.w.absent);
    return {
      w, from: x.off ? gone : fmtHours(r1(x.w.cap)), to: x.off ? gone : fmtHours(r1(cap)),
      load: x.off ? "" : cap > 0 ? fmtHours(r1(sim.load)) + " gepland · " + sim.pct + " %" : "niet inzetbaar",
      cls: x.off ? "t-mute" : cap > 0 ? TONE_CLS[cv.tone(sim.pct)] : sim.load >= 0.5 ? "t-err" : "t-mute",
    };
  });
  const withCap = effCap.length > 0 && effCap.every((x) => x !== null);
  const all = cp ? cp.weeks.flatMap((w) => w.absent) : [];
  const leave = mergeAbsences(all.filter((a) => a.kind === "verlof")), free = mergeAbsences(all.filter((a) => a.kind !== "verlof"));
  const kv: [string, string][] = cv && cp ? [
    ["Werkweek", fmtHours(r1(normal)) + (Math.abs(normal - cv.weekHours) < 0.05 ? ", standaard" : ", afwijkend")],
    ["Verlof", leave.length ? leave.map(spanText).join(", ") : "geen in deze weken"],
    ...(free.length ? [["Feestdagen", free.map(absenceText).join(", ")] as [string, string]] : []),
    ["Supportreservering", cp.supportPerWeek ? "≈ " + fmtHours(cp.supportPerWeek) + " per week" : "geen"],
    ...(cp.backlog.hours >= 0.05 ? [["Achterstand", fmtHours(cp.backlog.hours) + ", verdeeld over 4 weken"] as [string, string]] : []),
    ...(cp.noEstimate.length ? [["Zonder schatting", nOf(cp.noEstimate.length, "actieve taak", "actieve taken") + ", telt niet mee"] as [string, string]] : []),
  ] : [];

  const move = (t: Team | null) => {
    if ((t?.id ?? null) === (team?.id ?? null)) return;
    write.run({ run: () => teamsApi.moveMember(pid, t?.id ?? null), undo: () => teamsApi.moveMember(pid, team?.id ?? null), keys: KEYS }).catch(() => {});
  };
  const save = () => write.run({
    run: () => teamsApi.addInzet({ personId: pid, from: weeks[f.from].monday, to: f.to === "open" ? null : weeks[to].monday, pct: f.pct, kind: f.pct < 100 ? f.kind : "", note: f.pct < 100 ? f.note.trim() : "" }),
    undo: (r) => teamsApi.removeInzet(r.id as number),
    keys: KEYS,
  }).then(onClose, () => {});

  const effect = weeks.slice(f.from, to + 1).map((w) => { const e = periodAt(inzet, pid, w.monday); return { w, now: e ? e.pct : 100 }; });
  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="tp">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Teams · {team ? team.name : "zonder team"}</p><h2 id="tp">{person.name}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
            <span className={"ex-pill ex-pill--" + pill.cls}><span className="ex-dot" />{pill.text}</span>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs"><h3>Team</h3>
            <div className="p-quick">
              {[...teams, null].map((t) => (
                <button key={t?.id ?? "geen"} type="button" className={"p-tog p-tog-s" + ((t?.id ?? null) === (team?.id ?? null) ? " on" : "")} disabled={write.busy} onClick={() => move(t)}>{t ? t.name : NO_TEAM}</button>
              ))}
            </div>
          </div>
          {mine.length > 0 && (
            <div className="p-fs"><h3>Minder inzetbaar</h3>
              <div className="p-tm-per">
                {mine.map((e) => (
                  <div key={e.id} className="p-tm-pi">
                    <span><b>{e.pct} % inzetbaar · {periodText(e)}</b><small>{e.pct < 100 ? e.kind + (e.note ? ": " + e.note : "") : "hele week inzetbaar"}</small></span>
                    <button type="button" className="ex-btn-xs" disabled={write.busy} onClick={() => onRemove(e)}>Verwijderen</button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="p-fs"><h3>Nieuwe periode</h3>
            <div className="p-tm-f"><span className="ex-label">Van</span>
              <div className="p-quick">{weeks.map((w, i) => <button key={w.monday} type="button" className={"p-tog p-tog-s" + (f.from === i ? " on" : "")} onClick={() => set({ from: i, to: f.to === "open" ? "open" : Math.max(i, f.to) })}>{w.label}</button>)}</div>
            </div>
            <div className="p-tm-f"><span className="ex-label">Tot en met</span>
              <div className="p-quick">
                {weeks.map((w, i) => (i < f.from ? null : <button key={w.monday} type="button" className={"p-tog p-tog-s" + (f.to === i ? " on" : "")} onClick={() => set({ to: i })}>{w.label}</button>))}
                <button type="button" className={"p-tog p-tog-s" + (f.to === "open" ? " on" : "")} onClick={() => set({ to: "open" })}>doorlopend</button>
              </div>
            </div>
            <div className="p-tm-f"><span className="ex-label">Maximaal inzetbaar voor de planning</span>
              <div className="p-quick">{INZET_PCTS.map((v) => <button key={v} type="button" className={"p-tog p-tog-s" + (f.pct === v ? " on" : "")} onClick={() => set({ pct: v })}>{v} %</button>)}</div>
            </div>
            {f.pct < 100 && <>
              <div className="p-tm-f"><span className="ex-label">De rest van de week gaat naar</span>
                <div className="p-quick">{INZET_KINDS.map((k) => <button key={k} type="button" className={"p-tog p-tog-s" + (f.kind === k ? " on" : "")} onClick={() => set({ kind: k })}>{k}</button>)}</div>
              </div>
              <div className="p-tm-f"><span className="ex-label">Omschrijving</span><input className="ex-input" type="text" maxLength={200} placeholder="bijvoorbeeld opleiding Power BI" aria-label="Omschrijving" value={f.note} onChange={(e) => set({ note: e.target.value })} /></div>
            </>}
            <p className="p-tm-sum">{f.pct < 100 ? f.pct + " % van de week is inzetbaar voor de planning, " + (100 - f.pct) + " % gaat naar " + kind + "." : "De hele week is inzetbaar voor de planning; dit heft een eerdere periode op."}</p>
            {withCap ? (
              <div className="p-tm-eff">
                <div className="p-tm-eff-h"><span>Week</span><span>Nu planbaar</span><span>Wordt</span><span>Bezetting</span></div>
                {effCap.map((x) => x && (
                  <div key={x.w.monday} className="p-tm-eff-r">
                    <span><b>{x.w.label}</b><small>{ddmm(x.w.monday)}</small></span><span>{x.from}</span><span><b>{x.to}</b></span>
                    <span className={"p-tm-eff-l " + x.cls}>{x.load}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-tm-eff">
                <div className="p-tm-eff-h"><span>Week</span><span>Nu inzetbaar</span><span>Wordt</span><span>Reden</span></div>
                {effect.map(({ w, now }) => (
                  <div key={w.monday} className="p-tm-eff-r">
                    <span><b>{w.label}</b><small>{ddmm(w.monday)}</small></span><span>{now} %</span><span><b>{f.pct} %</b></span>
                    <span className={"p-tm-eff-l " + (f.pct < 100 ? "t-warn" : "t-ok")}>{f.pct < 100 ? kind : "volledig"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {cv && (
            <div className="p-fs"><h3>Beschikbaarheid</h3>
              {kv.map(([k, v]) => <div key={k} className="p-kv"><span>{k}</span><b>{v}</b></div>)}
              <p className="p-kd-note">{cp
                ? <>De werkweek past de superadmin aan in <Link className="ex-link" to="/instellingen">Instellingen</Link>; verlof en feestdagen komen uit Clockify. De bezetting telt de achterstand en de support mee.</>
                : firstName(person.name) + " staat niet in Clockify met hetzelfde e-mailadres: planbare uren, verlof en bezetting tellen niet mee."}</p>
            </div>
          )}
        </div>
        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>Voor {firstName(person.name)}, met naam en tijd in het logboek</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={onClose}>Sluiten</button>
            <button type="button" className="ex-btn ex-btn--primary" disabled={write.busy} onClick={save}><Icon name="check" size={15} />Periode opslaan</button>
          </span>
        </div>
      </aside>
    </>
  );
}

function TeamDrawer({ team, teams, people, teamOf, write, onClose }: {
  team: Team | null; teams: Team[]; people: Person[]; teamOf: Map<string, Team>; write: Write; onClose: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(team?.name || "");
  const [members, setMembers] = useState<Set<string>>(() => new Set(team?.members || []));
  const count = people.filter((p) => members.has(p.id)).length;
  const toggle = (id: string) => { const m = new Set(members); if (m.has(id)) m.delete(id); else m.add(id); setMembers(m); };

  const save = () => {
    const n = name.trim();
    if (!n) { toast.flash("Geef het team een naam"); return; }
    if (teams.some((t) => t.name.toLowerCase() === n.toLowerCase() && t.id !== team?.id)) { toast.flash("Er bestaat al een team " + n); return; }
    const list = [...members];
    if (!team) {
      write.run({ run: () => teamsApi.createTeam(n, list), undo: (r) => teamsApi.deleteTeam(r.id as number), keys: KEYS }).then(onClose, () => {});
      return;
    }
    // Ongedaan maken: naam en leden terug, en wie uit een ander team kwam, gaat daar weer naartoe
    const before = { name: team.name, members: team.members };
    const moved = list.filter((id) => !team.members.includes(id)).map((id) => ({ id, prev: teamOf.get(id)?.id ?? null })).filter((x) => x.prev !== null);
    write.run({
      run: () => teamsApi.updateTeam(team.id, { name: n, members: list }),
      undo: async () => { await teamsApi.updateTeam(team.id, before); for (const x of moved) await teamsApi.moveMember(x.id, x.prev); },
      keys: KEYS,
    }).then(onClose, () => {});
  };
  const remove = () => {
    if (!team) return;
    write.run({ run: () => teamsApi.deleteTeam(team.id), undo: () => teamsApi.createTeam(team.name, team.members), keys: KEYS }).then(onClose, () => {});
  };

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={onClose} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="tt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div style={{ minWidth: 0 }}><p className="ex-eyebrow" style={{ margin: 0 }}>Teams</p><h2 id="tt">{team ? "Team " + team.name : "Nieuw team"}</h2></div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={onClose}><Icon name="x" size={18} /></button>
          </div>
        </div>
        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-fs"><h3>Naam</h3><input className="ex-input p-tm-name" type="text" autoFocus maxLength={100} placeholder="bijvoorbeeld EDI" aria-label="Naam van het team" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} /></div>
          <div className="p-fs"><h3>Leden · {nOf(count, "lid", "leden")}</h3>
            <div className="p-tm-mem">
              {people.map((p) => {
                const on = members.has(p.id), cur = teamOf.get(p.id);
                const note = team && cur?.id === team.id ? "zit in dit team" : !cur ? "nog zonder team" : "nu in " + cur.name;
                return (
                  <label key={p.id} className={"p-opt s" + (on ? " on" : "")}>
                    <input type="checkbox" checked={on} onChange={() => toggle(p.id)} /><Av id={p.id} init={p.initials} cls="p-av s" /><span className="nm">{p.name}</span><span className="c">{note}</span>
                  </label>
                );
              })}
            </div>
            <p className="p-kd-note">Iemand zit in één team: aanvinken verplaatst die persoon naar dit team. Teams bepalen de groepering op het bord.</p>
          </div>
        </div>
        <div className="p-drawer-f">
          {team && <button type="button" className="ex-btn" disabled={write.busy} onClick={remove}>Team verwijderen</button>}
          <span style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button type="button" className="ex-btn" onClick={onClose}>Annuleren</button>
            <button type="button" className="ex-btn ex-btn--primary" disabled={write.busy} onClick={save}><Icon name="check" size={15} />Opslaan</button>
          </span>
        </div>
      </aside>
    </>
  );
}
